/**
 * Ranking context for Hunt card events — `ai_hunt_suggestion_viewed`,
 * `hunt_card_clicked` and `hunt_apply_approved`.
 *
 * Why (research PR #7640, scripts/research/feed-ranking-2026-10/README.md):
 * Hunt cards convert ~10x feed cards (21% lead to an outbound apply), but Hunt
 * logged no rank or position, so its ranker could not be compared with the
 * feed's. These props give each Hunt card the same shape the feed logs:
 * the policy that ranked it, its ordinal in the session, its score, and the
 * surface the session was entered from. Logging only: Hunt ranking is
 * unchanged.
 *
 * Pure (no server-only imports): read by the server actions and the client
 * card components alike.
 */

/** `rank_policy` of every Hunt card: Hunt's own fit ranker. */
export const HUNT_RANK_POLICY = "hunt_fit";

/**
 * Cookie the feed's Hunt entry card sets on click. Read by the Hunt server
 * actions (app zone, app.lokerdollar.com) and the Hunt client, so it is
 * scoped to the registrable domain in production. Not a personalization
 * cookie: the edge HTML cache strips it like any other unknown cookie.
 */
export const HUNT_ENTRY_COOKIE = "ld_hunt_entry";
/** Attribution window: a session entered from the card within 30 minutes. */
export const HUNT_ENTRY_MAX_AGE_S = 30 * 60;

export const HUNT_ENTRY_SURFACES = ["feed_hunt_card"] as const;
export type HuntEntrySurface = (typeof HUNT_ENTRY_SURFACES)[number];

export function parseHuntEntrySurface(value: unknown): HuntEntrySurface | null {
	return typeof value === "string" &&
		(HUNT_ENTRY_SURFACES as readonly string[]).includes(value)
		? (value as HuntEntrySurface)
		: null;
}

/** The entry surface named by a `Cookie` header / `document.cookie` string. */
export function huntEntrySurfaceFromCookieString(
	cookieString: string | null | undefined,
): HuntEntrySurface | null {
	if (!cookieString) return null;
	for (const part of cookieString.split(";")) {
		const eq = part.indexOf("=");
		if (eq < 0) continue;
		if (part.slice(0, eq).trim() !== HUNT_ENTRY_COOKIE) continue;
		return parseHuntEntrySurface(part.slice(eq + 1).trim());
	}
	return null;
}

/**
 * `Domain` for the entry cookie. On lokerdollar.com hosts it is the parent
 * domain, because the feed lives on the apex and Hunt on app.lokerdollar.com;
 * elsewhere (localhost, previews) undefined = host-only.
 */
export function huntEntryCookieDomain(hostname: string): string | undefined {
	return hostname === "lokerdollar.com" || hostname.endsWith(".lokerdollar.com")
		? ".lokerdollar.com"
		: undefined;
}

/**
 * 1-based ordinal of `jobId` among the cards this Hunt session has shown
 * (the session's insertion-ordered `seenJobIds`), or null when it is not
 * among them. A replayed card keeps its first ordinal.
 */
export function huntCardOrdinal(
	seenJobIds: Iterable<string>,
	jobId: string,
): number | null {
	let i = 0;
	for (const id of seenJobIds) {
		i++;
		if (id === jobId) return i;
	}
	return null;
}

export type HuntRankProps = {
	rank_policy: typeof HUNT_RANK_POLICY;
	card_ordinal: number | null;
	score: number | null;
	entry_surface: HuntEntrySurface | null;
};

export function huntRankProps(input: {
	score: number | null | undefined;
	cardOrdinal: number | null | undefined;
	entrySurface: HuntEntrySurface | null | undefined;
}): HuntRankProps {
	return {
		rank_policy: HUNT_RANK_POLICY,
		card_ordinal:
			typeof input.cardOrdinal === "number" && input.cardOrdinal > 0
				? input.cardOrdinal
				: null,
		score: typeof input.score === "number" ? input.score : null,
		entry_surface: input.entrySurface ?? null,
	};
}

/**
 * Record a card's session ordinal on the card itself, right after the
 * session's seen list takes it, so the persisted card carries it to the
 * client (`hunt_card_clicked`). Keeps an ordinal already stamped.
 */
export function stampHuntCardOrdinal(
	card: { jobId: string; cardOrdinal?: number },
	seenJobIds: Iterable<string>,
): void {
	if (typeof card.cardOrdinal === "number") return;
	const ordinal = huntCardOrdinal(seenJobIds, card.jobId);
	if (ordinal !== null) card.cardOrdinal = ordinal;
}
